import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { DetailsHistorieCardComponent } from '../../../components/details-historie-card/details-historie-card.component';
import { DetailsHistoriePanelDirective } from '../../../components/details-historie-card/details-historie-panel.directive';
import { PersonOption } from '../../../components/person-select/person-select.component';
import { SaveToastService } from '../../../components/save-toast/save-toast.service';
import { UebergabePanelComponent } from '../../../components/uebergabe-panel/uebergabe-panel.component';
import {
  aktiveStufe,
  aktivSeit,
  toUebergabeStufen,
  uebergabeFehlerKey,
  uebergebenGesperrt,
} from '../../../components/uebergabe-panel/uebergabe-kette';
import { VerlaufListeComponent } from '../../../components/verlauf-liste/verlauf-liste.component';
import {
  DetailsHistorieStatus,
  DetailsHistorieTab,
  UebergabeEntwurf,
  UebergabeHandOff,
  UebergabeStufe,
  VerlaufEintrag,
} from '../../../models/details-historie.model';
import { HandoverChain } from '../../../models/handover-chain.model';
import { TestPlanActivity, TestPlanActivityPayload } from '../../../models/test-plan-history.model';
import { TestPlanHistoryService } from '../../../services/test-plan-history.service';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';

const KEY = 'testPlans.form.history.';
const VORLAGEN = ['ergaenzen', 'reviewen', 'freigeben'] as const;
const FELDER = ['title', 'status', 'type', 'dueDate', 'assignee', 'tags'];
const TYPEN = [
  'CREATED',
  'RELEASE_CHANGED',
  'HANDED_OVER',
  'HANDOVER_CLOSED',
  'HANDOVER_REOPENED',
  'HANDOVER_SKIPPED',
  'AUTO_ADDED',
];
const VERLAUF_ANZAHL = 20;

const GEMEINSAME_FEHLER = 'common.detailsHistorie.uebergabe.fehler.';
const EIGENE_FEHLER = ['anSichSelbst', 'abgeschlossen', 'allgemein'];

export interface TestPlanAnleger {
  id: string;
  displayName: string;
}

interface VerlaufText {
  ereignis: string;
  detail: string | null;
}

@Component({
  selector: 'app-test-plan-details-historie',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    DetailsHistorieCardComponent,
    DetailsHistoriePanelDirective,
    UebergabePanelComponent,
    VerlaufListeComponent,
  ],
  templateUrl: './test-plan-details-historie.component.html',
  styleUrl: './test-plan-details-historie.component.scss',
})
export class TestPlanDetailsHistorieComponent {
  readonly projectId = input.required<string>();
  readonly planId = input<string | null>(null);
  readonly readonly = input<boolean>(false);
  readonly personOptions = input<PersonOption[]>([]);
  readonly anleger = input<TestPlanAnleger | null>(null);
  readonly notes = input<string>('');
  readonly notesControl = input.required<FormControl<string>>();

  protected readonly maxNotes = FIELD_LENGTHS.TEST_PLAN_NOTES_MAX;

  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly service = inject(TestPlanHistoryService);
  private readonly toast = inject(SaveToastService);
  private readonly sprache = toSignal(this.translate.onLangChange, { initialValue: null });
  private readonly geoeffnetAm = new Date().toISOString();

  readonly aktiverReiter = signal('');
  // Hält den Übergabe-Entwurf, weil der Reiterwechsel das Panel zerstört.
  readonly entwurf = signal<UebergabeEntwurf | null>(null);

  protected readonly kette = signal<HandoverChain | null>(null);
  protected readonly ketteStatus = signal<DetailsHistorieStatus>('bereit');
  protected readonly beschaeftigt = signal(false);
  protected readonly aktionsFehler = signal<string | null>(null);
  protected readonly verlaufStatus = signal<DetailsHistorieStatus>('bereit');
  private readonly aktivitaeten = signal<TestPlanActivity[]>([]);
  private readonly verlaufGesamt = signal(0);
  private ketteAbruf: Subscription | null = null;
  private verlaufAbruf: Subscription | null = null;

  protected readonly istBearbeiten = computed(() => !!this.planId());
  protected readonly seit = computed(() => aktivSeit(this.kette()));
  protected readonly verlaufGekuerzt = computed(() => this.verlaufGesamt() > VERLAUF_ANZAHL);
  private readonly inhaberId = computed(() => aktiveStufe(this.kette())?.userId ?? null);

  protected readonly stufen = computed<UebergabeStufe[]>(() => {
    if (this.istBearbeiten()) return toUebergabeStufen(this.kette());
    const anleger = this.anleger();
    return anleger
      ? [
          {
            stageId: 'neu',
            userId: anleger.id,
            displayName: anleger.displayName,
            createdAt: this.geoeffnetAm,
            skipped: false,
          },
        ]
      : [];
  });

  protected readonly sperrGrund = computed<string | null>(() => {
    this.sprache();
    return this.istBearbeiten() ? null : this.translate.instant(`${KEY}erstNachAnlegen`);
  });

  protected readonly uebergebenSperrGrund = computed<string | null>(() => {
    this.sprache();
    return uebergebenGesperrt(this.kette(), this.inhaberId())
      ? this.fehlerText(`${GEMEINSAME_FEHLER}bereitsWeitergegeben`)
      : null;
  });

  protected readonly vorlagen = computed<string[]>(() => {
    this.sprache();
    return VORLAGEN.map((key) => this.translate.instant(`${KEY}vorlagen.${key}`));
  });

  protected readonly verlaufEintraege = computed<VerlaufEintrag[]>(() => {
    this.sprache();
    return this.aktivitaeten().map((aktivitaet) => ({
      personName: this.verlaufPerson(aktivitaet),
      ...this.verlaufText(aktivitaet),
      zeit: aktivitaet.createdAt,
    }));
  });

  protected readonly reiter = computed<DetailsHistorieTab[]>(() => {
    this.sprache();
    const label = (id: string): string => this.translate.instant(`${KEY}reiter.${id}`);
    return [
      { id: 'uebergabe', label: label('uebergabe'), count: this.stufen().length || null },
      { id: 'verlauf', label: label('verlauf'), count: this.verlaufGesamt() || null },
      { id: 'notizen', label: label('notizen'), count: this.notes().trim() ? 1 : null },
    ];
  });

  constructor() {
    effect(() => {
      const id = this.planId();
      untracked(() => {
        this.kette.set(null);
        this.entwurf.set(null);
        this.aktionsFehler.set(null);
        this.aktivitaeten.set([]);
        this.verlaufGesamt.set(0);
        if (!id) return;
        this.ladeKette();
        this.ladeVerlauf();
      });
    });
  }

  ladeKette(): void {
    const id = this.planId();
    if (!id) return;
    this.ketteAbruf?.unsubscribe();
    this.ketteStatus.set('laedt');
    this.ketteAbruf = this.service
      .getHandovers(this.projectId(), id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (kette) => {
          this.kette.set(kette);
          this.ketteStatus.set('bereit');
        },
        error: () => this.ketteStatus.set('fehler'),
      });
  }

  ladeVerlauf(): void {
    const id = this.planId();
    if (!id) return;
    this.verlaufAbruf?.unsubscribe();
    this.verlaufStatus.set('laedt');
    this.verlaufAbruf = this.service
      .listActivities(this.projectId(), id, VERLAUF_ANZAHL)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (seite) => {
          this.aktivitaeten.set(seite.content);
          this.verlaufGesamt.set(seite.totalElements);
          this.verlaufStatus.set('bereit');
        },
        error: () => this.verlaufStatus.set('fehler'),
      });
  }

  protected uebergeben(handOff: UebergabeHandOff): void {
    const name = this.personOptions().find((person) => person.id === handOff.userId)?.displayName ?? '';
    this.fuehreAus(
      (projectId, id) => this.service.handOff(projectId, id, handOff),
      () => this.toast.show(this.translate.instant(`${KEY}uebergebenAn`, { name })),
    );
  }

  protected entwurfGeaendert(entwurf: UebergabeEntwurf | null): void {
    this.entwurf.set(entwurf);
    if (!entwurf) this.aktionsFehler.set(null);
  }

  protected abschliessen(): void {
    this.fuehreAus((projectId, id) => this.service.close(projectId, id));
  }

  protected wiederOeffnen(): void {
    this.fuehreAus((projectId, id) => this.service.reopen(projectId, id));
  }

  protected ueberspringen(stageId: string): void {
    this.fuehreAus((projectId, id) => this.service.skip(projectId, id, stageId));
  }

  private fuehreAus(
    aufruf: (projectId: string, planId: string) => Observable<HandoverChain>,
    danach?: () => void,
  ): void {
    const id = this.planId();
    if (!id || this.readonly() || this.beschaeftigt()) return;
    this.beschaeftigt.set(true);
    aufruf(this.projectId(), id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (kette) => {
          // Beim Bestandsplan ohne gespeicherte Stufe antwortet das BE ohne die Anzeige-Stufe, die nur GET liefert.
          if (kette.stages.length) this.kette.set(kette);
          else this.ladeKette();
          this.beschaeftigt.set(false);
          this.aktionsFehler.set(null);
          danach?.();
          this.ladeVerlauf();
        },
        error: (err: HttpErrorResponse) => {
          this.beschaeftigt.set(false);
          this.aktionsFehler.set(this.fehlerText(uebergabeFehlerKey(err)));
          // Sonst hielte das Panel eine später geladene fremde Stufe für das eigene Ergebnis und leerte den Entwurf.
          this.entwurf.update((entwurf) => entwurf && { ...entwurf, stufenBeimAbsenden: null });
        },
      });
  }

  private fehlerText(key: string): string {
    const name = key.slice(GEMEINSAME_FEHLER.length);
    return this.translate.instant(EIGENE_FEHLER.includes(name) ? `${KEY}fehler.${name}` : key, {
      objekt: this.translate.instant(`${KEY}objekt`),
    });
  }

  private verlaufPerson(aktivitaet: TestPlanActivity): string {
    const system = aktivitaet.type === 'AUTO_ADDED' && !aktivitaet.createdByName;
    return system ? this.translate.instant(`${KEY}system`) : (aktivitaet.createdByName ?? '');
  }

  private verlaufText({ type, payload }: TestPlanActivity): VerlaufText {
    switch (type) {
      case 'EDITED':
        return { ereignis: this.bearbeitet(payload?.field), detail: null };
      case 'SELECTION_CHANGED':
        return this.auswahlGeaendert(payload);
      case 'TEST_CASES_ADDED':
        return this.anzahlText('TEST_CASES_ADDED', 'plus', payload?.count ?? 0);
      case 'TEST_CASES_REMOVED':
        return this.anzahlText('TEST_CASES_REMOVED', 'minus', payload?.count ?? 0);
    }
    if (!TYPEN.includes(type)) return { ereignis: this.t('typen.unbekannt'), detail: null };
    return { ereignis: this.t(`typen.${type}`), detail: this.typDetail(type, payload) };
  }

  private typDetail(type: string, payload: TestPlanActivityPayload | null): string | null {
    switch (type) {
      case 'RELEASE_CHANGED':
        return this.t('detail.release', { von: payload?.from || '—', nach: payload?.to || '—' });
      case 'HANDED_OVER':
        return payload?.toName ? this.t('detail.uebergebenAn', { name: payload.toName }) : null;
      case 'HANDOVER_SKIPPED':
        return payload?.skippedName ?? null;
      case 'AUTO_ADDED':
        return payload?.keys?.length ? `+${payload.keys.join(', ')}` : null;
      default:
        return null;
    }
  }

  private bearbeitet(feld: string | undefined): string {
    return feld && FELDER.includes(feld)
      ? this.t('typen.EDITED', { feld: this.t(`felder.${feld}`) })
      : this.t('typen.bearbeitetOhneFeld');
  }

  // Ohne Begründung nennt die Story nur die Anzahl, sobald die Auswahl in eine Richtung geändert wurde.
  private auswahlGeaendert(payload: TestPlanActivityPayload | null): VerlaufText {
    const added = payload?.added ?? [];
    const removed = payload?.removed ?? [];
    const reason = payload?.reason?.trim();
    if (!reason && added.length && !removed.length) return this.anzahlText('TEST_CASES_ADDED', 'plus', added.length);
    if (!reason && removed.length && !added.length) {
      return this.anzahlText('TEST_CASES_REMOVED', 'minus', removed.length);
    }
    const schluessel = [
      ...(added.length ? [`+${added.join(', ')}`] : []),
      ...(removed.length ? [`−${removed.join(', ')}`] : []),
    ].join(' · ');
    const teile = [schluessel, reason ? this.t('detail.zitat', { text: reason }) : ''].filter(Boolean);
    return { ereignis: this.t('typen.SELECTION_CHANGED'), detail: teile.join(' — ') || null };
  }

  private anzahlText(type: string, richtung: 'plus' | 'minus', n: number): VerlaufText {
    const detail = n ? this.t(`detail.${richtung}${n === 1 ? 'One' : 'Other'}`, { n }) : null;
    return { ereignis: this.t(`typen.${type}`), detail };
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(`${KEY}${key}`, params);
  }
}
